import { generateSign, generateUUID } from '~/utils/common'

type ExpertRequestOptions = {
    method?: 'GET' | 'POST'
    params?: Record<string, string | number | boolean | undefined>
    body?: Record<string, unknown>
    token?: string | null
    locale?: string | null
    /**
     * 涉及用户资产、handoff 等写操作必须显式要求真实用户凭证。
     */
    requireUserToken?: boolean
}

const productionHosts = new Set([
    'growth.weryai.com',
    'www.growth.weryai.com',
    'weryai.com',
    'www.weryai.com',
    'weryai.com.cn',
    'www.weryai.com.cn',
])

const mainAppLocaleBySourceLocale: Record<string, string> = {
    en: 'en',
    zh: 'zh',
    tw: 'zh-tw',
    ja: 'ja',
    fr: 'fr',
    es: 'es',
    ko: 'ko',
    de: 'de',
    ar: 'ar',
    pt: 'pt',
}

const mainAppBcp47LocaleByRouteLocale: Record<string, string> = {
    en: 'en-US',
    zh: 'zh-CN',
    'zh-tw': 'zh-TW',
    ja: 'ja-JP',
    fr: 'fr-FR',
    es: 'es-ES',
    ko: 'ko-KR',
    de: 'de-DE',
    ar: 'ar-SA',
    pt: 'pt-BR',
}

function normalizeOrigin(value: string) {
    const normalized = value.trim().replace(/\/+$/, '')
    if (!normalized) return ''
    try {
        return new URL(normalized).origin
    } catch {
        return normalized
    }
}

function normalizeSourceLocale(sourceLocale?: string | null) {
    const locale = String(sourceLocale || '').trim().toLowerCase().replace(/_/g, '-')
    if (['tw', 'zh-tw', 'zh-hk', 'zh-hant'].includes(locale)) return 'tw'
    return locale.split('-')[0] || 'en'
}

/** 将 Growth 的 19 种语言映射到 Wery 主站实际支持的 10 种路由语言。 */
export function resolveExpertAgentMainAppLocale(sourceLocale?: string | null) {
    const locale = normalizeSourceLocale(sourceLocale)
    return mainAppLocaleBySourceLocale[locale] || 'en'
}

/** handoff payload 使用主站支持的完整 BCP-47 locale。 */
export function resolveExpertAgentMainAppBcp47Locale(sourceLocale?: string | null) {
    return mainAppBcp47LocaleByRouteLocale[
        resolveExpertAgentMainAppLocale(sourceLocale)
    ] || 'en-US'
}

export function normalizeExpertAgentMainAppReturnPath(
    returnPath: string,
    sourceLocale?: string | null
) {
    const source = normalizeSourceLocale(sourceLocale)
    const target = resolveExpertAgentMainAppLocale(source)

    const sourcePrefix = `/${source}`
    const suffix = returnPath.slice(sourcePrefix.length)
    if (
        !returnPath.startsWith(sourcePrefix) ||
        (suffix && !['/', '?', '#'].includes(suffix[0] || ''))
    ) {
        return returnPath
    }
    return `/${target}${suffix}`
}

/**
 * 专家 Agent 服务与当前创作服务是两个独立部署。未显式配置时，按当前站点环境选择
 * .ai 的测试或生产服务，避免把专家请求误发到 growth API。
 */
export function resolveExpertAgentApiBase() {
    const configured = String(
        useRuntimeConfig().public.expertAgentApiBase || ''
    ).trim()
    if (configured) return configured.replace(/\/+$/, '')

    if (
        import.meta.client &&
        window.location.protocol === 'https:' &&
        productionHosts.has(window.location.hostname)
    ) {
        return 'https://api.wery.ai'
    }

    return 'https://test-agent.wery.ai'
}

/** 专家页完成 handoff 后应跳转到的主站，不与 API Base 混用。 */
export function resolveExpertAgentMainAppOrigin() {
    const configured = String(
        useRuntimeConfig().public.expertAgentMainAppOrigin || ''
    ).trim()
    if (configured) return normalizeOrigin(configured)

    if (
        import.meta.client &&
        ['localhost', '127.0.0.1'].includes(window.location.hostname)
    ) {
        return 'http://localhost:3001'
    }

    if (
        import.meta.client &&
        window.location.protocol === 'https:' &&
        productionHosts.has(window.location.hostname)
    ) {
        return 'https://www.wery.ai'
    }

    // 测试 API 使用 test-agent.wery.ai；但主站前端部署在
    // test-agent.weryai.com，不能复用 API 域名，否则 /sso/callback 会 404。
    return 'https://test-agent.weryai.com'
}

/** 是否具备可签发 SSO ticket 的 Growth 登录态（含 cookie / 本地缓存）。 */
export function hasGrowthSsoSession(user?: {
    token?: string | null
    hasToken?: boolean
    isLoggedIn?: boolean
}) {
    if (user?.token || user?.hasToken || user?.isLoggedIn) return true
    if (!import.meta.client) return false
    try {
        return Boolean(localStorage.getItem('token'))
    } catch {
        return false
    }
}

/** 调用 wery.ai 专家 Agent 接口，并复用该服务要求的 app_sign 协议。 */
export async function expertAgentRequest<T>(
    path: string,
    options: ExpertRequestOptions = {}
) {
    const method = options.method || 'GET'
    const params: Record<string, string | number | boolean> = {
        ...(options.params || {}),
        app_key: '20006016',
        app_ts: Date.now(),
        req_id: generateUUID(),
        platform: 'WEB',
        ver_code: '10100',
        ver_name: '1.1.0',
    }
    const expertAgentAppSecret = String(
        useRuntimeConfig().public.expertAgentAppSecret || ''
    )
    params.app_sign = generateSign(
        method,
        path,
        params,
        options.body,
        {
            '20006016': expertAgentAppSecret,
        }
    )

    const locale = options.locale?.split(/[-_]/)[0] || 'en'
    const baseUrl = resolveExpertAgentApiBase()
    const suppliedToken = options.token?.trim() || ''
    // 用户资产 / handoff 必须归属当前用户，绝不能在缺少登录态时继续请求。
    if (options.requireUserToken && !suppliedToken) {
        throw new Error('EXPERT_AGENT_USER_TOKEN_REQUIRED')
    }
    // SSO handoff 暂存接口按 ticket 鉴权，显式保持匿名；其余接口只用调用方传入的真实用户 token。
    const isPublicSsoHandoff = path === '/api/v1/public/sso/handoff/store'
    const token = isPublicSsoHandoff ? '' : suppliedToken
    const headers: Record<string, string> = {
        'Accept-Language': locale,
        'X-Locale': locale,
    }
    if (token) headers.Authorization = token

    // 专家服务只用显式 Authorization；不要回退到跨域 cookie，
    // 否则去掉测试 token 后未登录请求会意外改走 credentials: include。
    return $fetch<T>(`${baseUrl}${path}`, {
        method,
        query: params,
        body: options.body,
        headers,
        credentials: 'omit',
    })
}
